import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { trackedObject } from '@ember/reactive/collections';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';
import {
  PrismButton,
  PrismButtonGroup,
  PrismIcon,
  PrismModal,
  PrismNumberInput,
  PrismRadioGroup,
  PrismSelectInput,
} from '@mylighthouse/prism-ember';
import { TOAST_TIMEOUT_LONG } from '@mylighthouse/prism-foundation';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { t } from 'ember-intl';
import { eq } from 'ember-truth-helpers';
import { REVERSE_ISO_DAYS } from 'frontend-utils/utils/constants/constant-values';
import GeneralFormsFormFieldsDatepickerFormField from 'lumen/components/general/forms/form-fields/datepicker-form-field';
import type HotelPricingManagerBulkUpdateRoute from 'lumen/routes/hotel/pricing-manager/bulk-update';
import type { HotelAndMarketServiceWithHotel } from 'lumen/services/hotel-and-market';
import type { RecommendationAndCarateObject } from 'lumen/services/pricing-assistant-handle-recommendation-actions-service';
import {
  BULK_UPDATE_MAX_FUTURE_YEARS,
  BULK_UPDATE_OPTIONS,
  BULK_UPDATE_PRICE_OPTIONS,
  type BulkUpdatePriceOption,
  type BulkUpdateRadioOption,
  NUMBER_INPUT_MAX_VALUE,
  PRICE_DIFFERENCE_TYPES,
  RECOMMENDATION_ACTION_STATES,
} from 'lumen/utils/constants/pricing-assistant-constants';
import type { ModelFrom } from 'lumen/utils/routes';

interface ViewDates {
  endDate: string | null;
  selectedDays: string[];
  startDate: string | null;
}

interface TrackingData {
  changeType?: string;
  eventTrigger: string;
  exactPrice?: number;
  isPriceManuallyEdited: boolean;
  priceDiffPercent?: number;
  priceValueDiff?: number;
}

interface HotelPricingAssistantModalsBulkUpdateModalSignature {
  Args: {
    bulkEndDate: string | null;
    bulkStartDate: string | null;
    model: ModelFrom<HotelPricingManagerBulkUpdateRoute>;
    onClose: () => void;
    updateQueryParams: (
      startDate: string | null,
      endDate: string | null,
    ) => void;
  };
}

export default class HotelPricingAssistantModalsBulkUpdateModal extends Component<HotelPricingAssistantModalsBulkUpdateModalSignature> {
  @service('hotel-and-market')
  declare hotelAndMarketService: HotelAndMarketServiceWithHotel;
  @service declare intl: Services['intl'];
  @service
  declare pricingAssistantHandleRecommendationActionsService: Services['pricing-assistant-handle-recommendation-actions-service'];

  @service declare toast: Services['toast'];
  @service declare usageAnalytics: Services['usage-analytics'];

  @tracked attemptedSubmit = false;
  @tracked editPriceChangeByValue: number | null = null;
  @tracked editPriceExactValue: number | null = null;
  @tracked isPmsRequestLoading = false;

  @tracked selectedEditPriceChangeByTypeOption =
    PRICE_DIFFERENCE_TYPES.RELATIVE;
  @tracked selectedEditPriceOption: BulkUpdatePriceOption =
    BULK_UPDATE_PRICE_OPTIONS.CHANGE_BY;
  @tracked selectedView: BulkUpdateRadioOption =
    BULK_UPDATE_OPTIONS.ACCEPT_RECOMMENDATIONS;

  views = trackedObject<Record<BulkUpdateRadioOption, ViewDates>>({
    [BULK_UPDATE_OPTIONS.ACCEPT_RECOMMENDATIONS]: {
      startDate: null,
      endDate: null,
      selectedDays: ['1', '2', '3', '4', '5', '6', '7'],
    },
    [BULK_UPDATE_OPTIONS.EDIT_PRICES]: {
      startDate: null,
      endDate: null,
      selectedDays: ['1', '2', '3', '4', '5', '6', '7'],
    },
  });

  get carecommendationAndCaratePairs(): RecommendationAndCarateObject[] {
    let filteredPairs: RecommendationAndCarateObject[] | undefined = [];

    if (this.selectedView === BULK_UPDATE_OPTIONS.ACCEPT_RECOMMENDATIONS) {
      filteredPairs = this.args.model.carecommendationsTaskResult.value
        ?.filter((recommendation) => {
          return this.selectedDays.includes(
            recommendation.fromDate.isoWeekday().toString(),
          );
        })
        .map((recommendation) => ({
          carecommendation: recommendation,
          carate: recommendation.carate,
        }));
    } else {
      filteredPairs = this.ownHotelPricingAssistantRateObjects
        ?.filter((carate) => {
          return this.selectedDays.includes(
            carate.period.day.date.isoWeekday().toString(),
          );
        })
        .map((carate) => ({
          carecommendation: null,
          carate,
        }));
    }

    return filteredPairs ?? [];
  }

  get currentViewDates(): ViewDates {
    return this.views[this.selectedView];
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get editPriceChangeByType() {
    return this.editPriceChangeByTypeOptions.find(
      (option) => option.id === this.selectedEditPriceChangeByTypeOption,
    );
  }

  @cached
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get editPriceChangeByTypeOptions() {
    return [
      {
        id: PRICE_DIFFERENCE_TYPES.RELATIVE,
        label: this.intl.t('general.percentage'),
      },
      {
        id: PRICE_DIFFERENCE_TYPES.ABSOLUTE,
        label: this.hotelAndMarketService.hotel.currencyCode,
      },
    ];
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get editPriceChangeByValueLabel() {
    return this.selectedEditPriceChangeByTypeOption ===
      PRICE_DIFFERENCE_TYPES.RELATIVE
      ? '%'
      : this.hotelAndMarketService.hotel.currencySymbol;
  }

  get editPriceChangeByValueMinimum(): number | undefined {
    return this.selectedEditPriceChangeByTypeOption ===
      PRICE_DIFFERENCE_TYPES.RELATIVE
      ? -100
      : undefined;
  }

  get editPriceChangeByWarning(): boolean {
    return (
      this.selectedEditPriceOption === BULK_UPDATE_PRICE_OPTIONS.CHANGE_BY &&
      this.editPriceChangeByValue === 0
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get editPriceExactValueWarning() {
    if (this.editPriceExactValue === null) {
      return '';
    }

    const { minPrice, maxPrice } = this.userSetMinMaxFromCarates;

    if (this.editPriceExactValue < minPrice) {
      return this.intl.t(
        'pricing_assistant.edit_price.warning.lower_than_settings',
        {
          minSettingsPrice: this.hotelAndMarketService.formatRate(minPrice),
          htmlSafe: true,
        },
      );
    }

    if (this.editPriceExactValue > maxPrice) {
      return this.intl.t(
        'pricing_assistant.edit_price.warning.higher_than_settings',
        {
          maxSettingsPrice: this.hotelAndMarketService.formatRate(maxPrice),
          htmlSafe: true,
        },
      );
    }

    return '';
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get endDate() {
    if (!this.currentViewDates.endDate) {
      return null;
    }
    return this.hotelAndMarketService.toLocalTime(
      this.currentViewDates.endDate,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get filteredRecommendationsCount() {
    return this.carecommendationAndCaratePairs.length > 100
      ? '100+'
      : this.carecommendationAndCaratePairs.length;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedDaySegmentedButtons() {
    const days = [];
    let dow = dayjs().startOf('week');

    while (dow.isSameOrBefore(dayjs().endOf('week'))) {
      const weekdayId = dow.isoWeekday();
      const isoWeekday = REVERSE_ISO_DAYS[weekdayId];

      days.push({
        id: weekdayId.toString(),
        value: this.intl.t(`days_short.${isoWeekday}`),
        longValue: this.intl.t(`days.${isoWeekday}`),
        isSelected: this.currentViewDates.selectedDays.includes(
          weekdayId.toString(),
        ),
      });

      dow = dow.add(1, 'day');
    }

    return days;
  }

  get isDaysOfWeekInvalid(): boolean {
    return this.selectedDays.length === 0;
  }

  get isEditPriceValueValid(): boolean {
    if (this.selectedEditPriceOption === BULK_UPDATE_PRICE_OPTIONS.CHANGE_BY) {
      return this.editPriceChangeByValue !== null;
    }

    return this.editPriceExactValue !== null;
  }

  get isEndDateAfterStartDate(): boolean {
    if (this.isDateEmpty(this.startDate) || this.isDateEmpty(this.endDate)) {
      return false;
    }
    return this.endDate.isSameOrAfter(this.startDate);
  }

  get isEndDateValid(): boolean {
    const date = this.endDate;
    if (!date || !date.isValid()) {
      return false;
    }
    if (this.startDate && date.isBefore(this.startDate)) {
      return false;
    }
    return date.isSameOrBefore(this.maxDate);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isFormValid() {
    let isValid =
      this.isStartDateValid &&
      this.isEndDateValid &&
      this.isEndDateAfterStartDate;

    if (this.selectedView === BULK_UPDATE_OPTIONS.EDIT_PRICES) {
      isValid = isValid && this.isEditPriceValueValid;
    }

    return isValid;
  }

  get isLoading(): boolean {
    return (
      this.args.model.caratesTaskResult.isRunning ||
      this.args.model.carecommendationsTaskResult.isRunning ||
      this.isPmsRequestLoading
    );
  }

  get isStartDateValid(): boolean {
    const date = this.startDate;
    if (!date || !date.isValid()) {
      return false;
    }
    return date.isSameOrAfter(dayjs(), 'day');
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get maxDate() {
    return this.hotelAndMarketService.toLocalTime(
      dayjs().add(BULK_UPDATE_MAX_FUTURE_YEARS, 'year').format('YYYY-MM-DD'),
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get minDate() {
    return this.hotelAndMarketService.toLocalTime(dayjs().format('YYYY-MM-DD'));
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get minEndDate() {
    return this.startDate ?? this.minDate;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get ownHotelPricingAssistantRateObjects() {
    return this.args.model.caratesTaskResult.value?.flatMap((period) => {
      return period.ownHotelCarates;
    });
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get selectedDays() {
    return this.currentViewDates.selectedDays;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get selectedDaySegmentedButtons() {
    return this.formattedDaySegmentedButtons
      .filter((button) => button.isSelected)
      .map((button) => button.id);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get startDate() {
    if (!this.currentViewDates.startDate) {
      return null;
    }
    return this.hotelAndMarketService.toLocalTime(
      this.currentViewDates.startDate,
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get userSetMinMaxFromCarates() {
    const carates = this.carecommendationAndCaratePairs.map(
      ({ carate }) => carate,
    );

    const minPrice = carates.reduce((min, carate) => {
      if (
        carate?.userSetMinimum === undefined ||
        carate.userSetMinimum === null
      ) {
        return min;
      }

      return Math.min(carate.userSetMinimum, min);
    }, Infinity);

    const maxPrice = carates.reduce((max, carate) => {
      if (
        carate?.userSetMaximum === undefined ||
        carate.userSetMaximum === null
      ) {
        return max;
      }

      return Math.max(carate.userSetMaximum, max);
    }, -Infinity);

    // Handle case where no valid min/max found
    return {
      minPrice: minPrice === Infinity ? 0 : minPrice,
      maxPrice: maxPrice === -Infinity ? NUMBER_INPUT_MAX_VALUE : maxPrice,
    };
  }

  constructor(
    owner: Owner,
    args: HotelPricingAssistantModalsBulkUpdateModalSignature['Args'],
  ) {
    super(owner, args);
    this.views[this.selectedView].startDate = this.args.bulkStartDate;
    this.views[this.selectedView].endDate = this.args.bulkEndDate;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  async handleBulkPushRequest() {
    if (!this.carecommendationAndCaratePairs) {
      return null;
    }

    // Calculate new prices based on the selected edit price option
    const newPrices: Record<string, number | null> = {};
    if (this.selectedView === BULK_UPDATE_OPTIONS.EDIT_PRICES) {
      this.carecommendationAndCaratePairs.forEach(({ carate }) => {
        const carateId = carate.id;
        if (
          this.selectedEditPriceOption ===
            BULK_UPDATE_PRICE_OPTIONS.CHANGE_BY &&
          this.editPriceChangeByValue
        ) {
          if (
            this.selectedEditPriceChangeByTypeOption ===
            PRICE_DIFFERENCE_TYPES.RELATIVE
          ) {
            // @ts-expect-error: Type 'null' cannot be used as an index type.
            newPrices[carateId] =
              carate.value * (1 + this.editPriceChangeByValue / 100);
          } else {
            // @ts-expect-error: Type 'null' cannot be used as an index type.
            newPrices[carateId] = carate.value + this.editPriceChangeByValue;
          }
        } else if (
          this.selectedEditPriceOption === BULK_UPDATE_PRICE_OPTIONS.EXACT &&
          this.editPriceExactValue
        ) {
          // @ts-expect-error: Type 'null' cannot be used as an index type.
          newPrices[carateId] = this.editPriceExactValue;
        }
      });
    }

    return this.pricingAssistantHandleRecommendationActionsService.handleCreateRecommendationActionBulkPush(
      this.carecommendationAndCaratePairs,
      RECOMMENDATION_ACTION_STATES.ACCEPTED,
      newPrices,
    );
  }

  isDateEmpty(date: Dayjs | null): date is null {
    return !date;
  }

  @action
  changeEditPriceOption(event: Event): void {
    this.selectedEditPriceOption = (event.target as HTMLInputElement)
      .value as BulkUpdatePriceOption;
  }

  @action
  changeView(event: Event): void {
    this.attemptedSubmit = false;
    this.selectedView = (event.target as HTMLInputElement)
      .value as BulkUpdateRadioOption;

    // Update query params to reflect the new selected view's dates
    this.args.updateQueryParams(
      this.currentViewDates.startDate,
      this.currentViewDates.endDate,
    );
  }

  @action
  closeModal(): void {
    this.args.onClose();
  }

  @action
  endSelection(value: Dayjs | null): void {
    const formattedDate = value ? value.format('YYYY-MM-DD') : null;
    this.views[this.selectedView] = {
      ...this.currentViewDates,
      endDate: formattedDate,
    };
    this.args.updateQueryParams(this.currentViewDates.startDate, formattedDate);
  }

  @action
  onDaySegmentedButtonClick(selectedButtonIds: string | string[]): void {
    this.views[this.selectedView] = {
      ...this.currentViewDates,
      selectedDays: Array.isArray(selectedButtonIds)
        ? selectedButtonIds
        : [selectedButtonIds],
    };
  }

  @action
  async onSubmit(): Promise<void> {
    this.attemptedSubmit = true;
    if (this.isFormValid) {
      if (this.carecommendationAndCaratePairs.length > 0) {
        this.isPmsRequestLoading = true;

        const result = await this.handleBulkPushRequest();

        if (result?.isRequestSuccess) {
          const pricesUpdatedText = this.intl.t(
            'pricing_assistant.bulk_update.prices_updated',
            {
              count: this.carecommendationAndCaratePairs.length,
            },
          );
          this.toast.success(pricesUpdatedText, {
            timeout: TOAST_TIMEOUT_LONG,
            showIcon: true,
          });
        }
        this.isPmsRequestLoading = false;

        const trackingData: TrackingData = {
          eventTrigger: 'bulk-update',
          isPriceManuallyEdited:
            this.selectedView === BULK_UPDATE_OPTIONS.EDIT_PRICES,
        };

        // Add specific tracking data for edit prices mode
        if (this.selectedView === BULK_UPDATE_OPTIONS.EDIT_PRICES) {
          if (
            this.selectedEditPriceOption ===
              BULK_UPDATE_PRICE_OPTIONS.CHANGE_BY &&
            this.editPriceChangeByValue !== null
          ) {
            // eslint-disable-next-line max-depth
            if (
              this.selectedEditPriceChangeByTypeOption ===
              PRICE_DIFFERENCE_TYPES.RELATIVE
            ) {
              trackingData.changeType = 'percentage';
              trackingData.priceDiffPercent = this.editPriceChangeByValue / 100;
            } else {
              trackingData.changeType = 'currency';
              trackingData.priceValueDiff = this.editPriceChangeByValue;
            }
          } else if (
            this.selectedEditPriceOption === BULK_UPDATE_PRICE_OPTIONS.EXACT &&
            this.editPriceExactValue !== null
          ) {
            trackingData.changeType = 'specific price';
            trackingData.exactPrice = this.editPriceExactValue;
          }
        }

        this.usageAnalytics.track('Bulk Update Sent', trackingData);
      }
      this.closeModal();
    }
  }

  @action
  startSelection(value: Dayjs | null): void {
    const formattedDate = value ? value.format('YYYY-MM-DD') : null;
    this.views[this.selectedView] = {
      ...this.currentViewDates,
      startDate: formattedDate,
    };
    this.args.updateQueryParams(formattedDate, this.currentViewDates.endDate);
  }

  @action
  updateEditPriceChangeByType(option: { id: string; label: string }): void {
    this.selectedEditPriceChangeByTypeOption = option.id;

    if (
      this.selectedEditPriceChangeByTypeOption ===
      PRICE_DIFFERENCE_TYPES.RELATIVE
    ) {
      this.editPriceChangeByValue =
        this.editPriceChangeByValue === null
          ? null
          : Math.max(-100, this.editPriceChangeByValue);
    }
  }

  @action
  updateEditPriceChangeByValue(value: number | null): void {
    this.editPriceChangeByValue = value;
  }

  @action
  updateEditPriceExactPrice(value: number | null): void {
    this.editPriceExactValue = value;
  }

  <template>
    <PrismModal
      @onClose={{this.closeModal}}
      @title={{t "general.bulk_update"}}
      class="bulk-update-modal"
    >
      <:default>
        <div class="modal--small modal--padded flex flex-col gap-400">
          <div>
            <label class="text-300--medium">{{t
                "pricing_assistant.bulk_update.i_want_to"
              }}</label>
            <PrismRadioGroup
              @isHorizontal={{true}}
              @name="bulk-update"
              @onChange={{this.changeView}}
              @value={{this.selectedView}}
              class="mt-200"
              as |RadioGroup|
            >
              <RadioGroup.input
                @value={{BULK_UPDATE_OPTIONS.ACCEPT_RECOMMENDATIONS}}
              >
                {{t "pricing_assistant.bulk_update.accept_recommendations"}}
              </RadioGroup.input>
              <RadioGroup.input @value={{BULK_UPDATE_OPTIONS.EDIT_PRICES}}>
                {{t "pricing_assistant.bulk_update.edit_prices"}}
              </RadioGroup.input>
            </PrismRadioGroup>
          </div>

          <div class="date-range-container flex w-full flex-col gap-400">
            <div class="flex gap-400">
              {{! The datepicker is intentionally duplicated. This is to ensure that validation errors are displayed correctly when focusing and blurring the input fields,
  as these are in the datepicker itself and can't be controlled in this template.
  This approach prevents errors from persisting when switching between the "Accept Recommendations" and "Edit Prices" views. }}
              {{#if
                (eq
                  this.selectedView BULK_UPDATE_OPTIONS.ACCEPT_RECOMMENDATIONS
                )
              }}
                <div class="w-full">
                  <GeneralFormsFormFieldsDatepickerFormField
                    {{! @glint-expect-error: Incorrect type }}
                    @datePickerMaxDate={{this.endDate}}
                    @datePickerMinDate={{this.minDate}}
                    @isTouched={{this.attemptedSubmit}}
                    @label={{t "events.start_date"}}
                    @onSelectDate={{this.startSelection}}
                    @removePadding={{true}}
                    @selectedDate={{this.startDate}}
                  />
                </div>
                <div class="w-full">
                  <GeneralFormsFormFieldsDatepickerFormField
                    {{! @glint-expect-error: Incorrect type }}
                    @datePickerMaxDate={{this.maxDate}}
                    @datePickerMinDate={{this.minEndDate}}
                    @isTouched={{this.attemptedSubmit}}
                    @label={{t "events.end_date"}}
                    @onSelectDate={{this.endSelection}}
                    @removePadding={{true}}
                    @selectedDate={{this.endDate}}
                  />
                </div>
              {{else}}
                <div class="w-full">
                  <GeneralFormsFormFieldsDatepickerFormField
                    {{! @glint-expect-error: Incorrect type }}
                    @datePickerMaxDate={{this.endDate}}
                    @datePickerMinDate={{this.minDate}}
                    @isTouched={{this.attemptedSubmit}}
                    @label={{t "events.start_date"}}
                    @onSelectDate={{this.startSelection}}
                    @removePadding={{true}}
                    @selectedDate={{this.startDate}}
                  />
                </div>
                <div class="w-full">
                  <GeneralFormsFormFieldsDatepickerFormField
                    {{! @glint-expect-error: Incorrect type }}
                    @datePickerMaxDate={{this.maxDate}}
                    @datePickerMinDate={{this.minEndDate}}
                    @isTouched={{this.attemptedSubmit}}
                    @label={{t "events.end_date"}}
                    @onSelectDate={{this.endSelection}}
                    @removePadding={{true}}
                    @selectedDate={{this.endDate}}
                  />
                </div>
              {{/if}}
            </div>
            <PrismButtonGroup
              @isSmall={{true}}
              @onChange={{this.onDaySegmentedButtonClick}}
              @value={{this.selectedDays}}
              class="w-full"
              as |ButtonGroup|
            >
              {{#each
                this.formattedDaySegmentedButtons
                as |formattedDaySegmentedButton|
              }}
                <ButtonGroup.button
                  @label={{formattedDaySegmentedButton.value}}
                  @value={{formattedDaySegmentedButton.id}}
                  class="w-full"
                />
              {{/each}}
            </PrismButtonGroup>
            {{#if this.isDaysOfWeekInvalid}}
              <div class="flex gap-200">
                <PrismIcon
                  @color="critical"
                  @name="circle-information-filled"
                  @size="small"
                />
                <p class="text-200--medium text-critical-default">{{t
                    "pricing_assistant.bulk_update.error.day_of_week"
                  }}</p>
              </div>
            {{/if}}
          </div>

          {{#if (eq this.selectedView BULK_UPDATE_OPTIONS.EDIT_PRICES)}}
            <div class="divider-line m-0"></div>
            <PrismRadioGroup
              @name="bulk-update-price-type"
              @onChange={{this.changeEditPriceOption}}
              @value={{this.selectedEditPriceOption}}
              as |RadioGroup|
            >
              <RadioGroup.input @value={{BULK_UPDATE_PRICE_OPTIONS.CHANGE_BY}}>
                <div class="flex flex-col gap-200">
                  {{t "pricing_assistant.bulk_update.change_price_by"}}
                  {{#if
                    (eq
                      this.selectedEditPriceOption
                      BULK_UPDATE_PRICE_OPTIONS.CHANGE_BY
                    )
                  }}
                    <div class="flex flex-col gap-100">
                      <div class="flex gap-200">
                        <div class="price-input">
                          <PrismNumberInput
                            @inlineLabel={{this.editPriceChangeByValueLabel}}
                            @isTouched={{this.attemptedSubmit}}
                            @max={{NUMBER_INPUT_MAX_VALUE}}
                            @min={{this.editPriceChangeByValueMinimum}}
                            @onValueChange={{this.updateEditPriceChangeByValue}}
                            @removePadding={{true}}
                            @required={{true}}
                            @requiredErrorMessage={{t
                              "pricing_assistant.bulk_update.relative_price_input.error"
                            }}
                            @value={{this.editPriceChangeByValue}}
                          />
                        </div>
                        <PrismSelectInput
                          @onChange={{this.updateEditPriceChangeByType}}
                          @options={{this.editPriceChangeByTypeOptions}}
                          @value={{this.editPriceChangeByType}}
                        />
                      </div>
                      {{#if this.editPriceChangeByWarning}}
                        <div class="flex gap-100">
                          <PrismIcon
                            @color="warning"
                            @name="warning"
                            @size="small"
                          />
                          <p class="text-200--medium text-warning-default">
                            {{t
                              "pricing_assistant.bulk_update.relative_price_input.warning"
                            }}
                          </p>
                        </div>
                      {{/if}}
                    </div>
                  {{/if}}
                </div>
              </RadioGroup.input>
              <RadioGroup.input @value={{BULK_UPDATE_PRICE_OPTIONS.EXACT}}>
                <div class="flex flex-col gap-200">
                  {{t "pricing_assistant.bulk_update.enter_exact_price"}}
                  {{#if
                    (eq
                      this.selectedEditPriceOption
                      BULK_UPDATE_PRICE_OPTIONS.EXACT
                    )
                  }}
                    <div class="flex flex-col gap-100">
                      <div class="price-input">
                        <PrismNumberInput
                          @inlineLabel={{this.hotelAndMarketService.hotel.currencySymbol}}
                          @isTouched={{this.attemptedSubmit}}
                          @max={{NUMBER_INPUT_MAX_VALUE}}
                          @min={{0}}
                          @onValueChange={{this.updateEditPriceExactPrice}}
                          @removePadding={{true}}
                          @required={{true}}
                          @requiredErrorMessage={{t
                            "pricing_assistant.bulk_update.relative_price_input.error"
                          }}
                          @value={{this.editPriceExactValue}}
                        />
                      </div>
                      {{#if this.editPriceExactValueWarning}}
                        <div class="flex w-full gap-100">
                          <PrismIcon
                            @color="warning"
                            @name="warning"
                            @size="small"
                          />
                          <p class="text-200--medium text-warning-default">
                            {{this.editPriceExactValueWarning}}
                          </p>
                        </div>
                      {{/if}}
                    </div>
                  {{/if}}
                </div>
              </RadioGroup.input>
            </PrismRadioGroup>
          {{/if}}
        </div>
      </:default>
      <:footer>
        <div class="settings-modal__footer flex justify-end">
          <PrismButton
            @iconBefore="send"
            @isLoading={{this.isLoading}}
            @label={{t
              "pricing_assistant.bulk_update.send_to_pms"
              count=this.filteredRecommendationsCount
            }}
            @onClick={{this.onSubmit}}
          />
        </div>
      </:footer>
    </PrismModal>
  </template>
}
